import { ButtonVariantOption, ButtonColorOption } from "@/components/Button";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import {
  MAP_BUTTON_HEIGHT,
  MAP_CONTROL_TEXT_SX,
  MAP_GRID_ROW_HEIGHT_PX,
} from "@/features/map/constants/controlSize";
import {
  type PropertySelectionRow,
  type RealEstateSearchResult,
} from "../types";
import {
  type GridCellParams,
  type GridColDef,
  type GridSelectionModel,
  type GridRenderCellParams,
  type GridColumnHeaderParams,
  type GridSortModel,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridPageSelector,
  gridPageSizeSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  alpha,
} from "@mui/material";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CustomFooter } from "./CustomFooter";
import {
  convertFullWidthDigitsAndHyphenToHalfWidth,
  convertTypeEnglishToJapanese,
  formatArea,
} from "../utils";
import { TagDetailModal } from "./TagDetailModal";
import { RemarksDetailModal } from "./RemarksDetailModal";
import { clearActivePopup } from "../utils/tagSync/activePopupManager";
import { createPropertySelectionEventHandlers } from "../utils/propertySelectionEventDispatchers";
import { useMapControl } from "../contexts/MapControlContext";
import { AdditionalFieldsModal } from "./AdditionalFieldsModal";
import { useAdditionalFieldsConfig } from "../hooks/useAdditionalFieldsConfig";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  convertStringToAdditionalFields,
  convertFormDataToAdditionalFields,
} from "../utils/additionalFieldsHelpers";

interface IProps {
  rows: PropertySelectionRow[];
  setRows: React.Dispatch<React.SetStateAction<PropertySelectionRow[]>>;
  realEstateSearchResult?: RealEstateSearchResult[];
  crm?: boolean;
  /** 表の高さ。親の高さに合わせる時は "100%" */
  height?: number | string;
  /** 地図のピンと対応させて色を付ける行（行を押した時・ピンを押した時）。その行が見える所までスクロールする */
  focusedRowId?: number | null;
  /** 行を押した時（チェックボックス・リンク・ボタンを押した時は呼ばない） */
  onRowFocus?: (row: PropertySelectionRow) => void;
  /** 表の上に、チェックした物件をまとめて削除するボタンを出すか（物件一覧の拡大モーダル） */
  bulkDeletable?: boolean;
}

/** 地図のピンと対応させている行に付ける class */
const FOCUSED_ROW_CLASS = "property-row--focused";

/** 押しても行を選んだことにしない要素（リンク・ボタン・チェックボックスなど、それぞれの操作を優先する） */
const INTERACTIVE_ELEMENT_SELECTOR =
  "a, button, input, label, [role='button'], [role='checkbox']";

export const PropertySelectionGrid: React.FC<IProps> = (props) => {
  const {
    rows,
    setRows,
    realEstateSearchResult,
    crm = false,
    height = 400,
    focusedRowId = null,
    onRowFocus,
    bulkDeletable = false,
  } = props;
  const isMobile = useIsMobile();

  const apiRef = useGridApiRef();

  // 対応させた行が見える所までスクロールする（別のページにあればそのページを開く）。
  // 地図クリックで足した行は表に入るのが少し後になるので、見つかるまで行の変化を待つ
  const scrolledRowIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (focusedRowId === null) {
      scrolledRowIdRef.current = null;
      return;
    }
    if (scrolledRowIdRef.current === focusedRowId) return;
    const index = apiRef.current.getSortedRowIds().indexOf(focusedRowId);
    if (index < 0) return;
    scrolledRowIdRef.current = focusedRowId;
    const page = Math.floor(index / gridPageSizeSelector(apiRef));
    if (gridPageSelector(apiRef) !== page) apiRef.current.setPage(page);
    // ページを替えた直後は行の位置がまだ決まっていないので、描き終わってから動かす
    requestAnimationFrame(() => {
      apiRef.current.scrollToIndexes({ rowIndex: index });
    });
  }, [apiRef, focusedRowId, rows]);

  const handleCellClick = (
    params: GridCellParams,
    event: React.MouseEvent
  ): void => {
    if (!onRowFocus) return;
    if (params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field) return;
    if ((event.target as Element).closest(INTERACTIVE_ELEMENT_SELECTOR)) return;
    const row = rows.find((r) => r.id === params.id);
    if (row) onRowFocus(row);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>(() =>
    rows.filter((row) => row.selected).map((row) => row.id)
  );

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedRowForTags, setSelectedRowForTags] =
    useState<PropertySelectionRow | null>(null);

  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [selectedRowForRemarks, setSelectedRowForRemarks] =
    useState<PropertySelectionRow | null>(null);

  const [additionalFieldsModalOpen, setAdditionalFieldsModalOpen] =
    useState(false);
  const [selectedRowForAdditionalFields, setSelectedRowForAdditionalFields] =
    useState<PropertySelectionRow | null>(null);

  // Context経由で地図を操作する関数を取得
  const mapControl = useMapControl();

  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();

  /**
   * 表題部検索ピン専用: 配列内のインデックスを取得
   *
   * 表題部検索ピンは複数の結果が配列で存在するため、
   * どの結果を更新すべきかインデックスで特定する必要がある
   *
   * @param row - 行データ
   * @returns 表題部検索の場合は配列インデックス、それ以外はnull
   */
  const findRealEstateSearchIndex = useCallback(
    (row: PropertySelectionRow): number | null => {
      // 表題部検索ピン以外は対象外
      if (!realEstateSearchResult || row.pinType !== "real-estate-search") {
        return null;
      }

      // 地番などの情報から配列内の位置を特定
      const index = realEstateSearchResult.findIndex(
        (item) =>
          item.bookType === row.bookType &&
          item.prefName === row.prefName &&
          item.location === row.location &&
          item.chibanKaokuNumber === row.chibanKaokuNumber
      );

      return index !== -1 ? index : null;
    },
    [realEstateSearchResult]
  );

  const findReceptionBookIndex = useCallback(
    (row: PropertySelectionRow): number | null => {
      if (row.pinType !== "reception-book") {
        return null;
      }

      // rowsの中で受付帳行のみを抽出し、その中での位置を特定
      const receptionBookRows = rows.filter(
        (r) => r.pinType === "reception-book"
      );
      const index = receptionBookRows.findIndex((r) => r.id === row.id);

      return index !== -1 ? index : null;
    },
    [rows]
  );

  useEffect(() => {
    setSelectedRowIds(rows.filter((row) => row.selected).map((row) => row.id));
  }, [rows]);

  /**
   * グリッドの行選択状態変更ハンドラー
   *
   * DataGridの選択状態が変更された際に呼び出されます。
   * 選択されたIDリストを内部状態に保持し、各行のselectedフラグを更新します。
   *
   * @param {GridSelectionModel} newSelectionModel - 新しく選択された行のIDリスト
   * @returns {void}
   */
  const handleRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      setSelectedRowIds(newSelectionModel);
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          selected: newSelectionModel.includes(row.id),
        }))
      );
    },
    [setRows]
  );

  /**
   * グリッドの行ソートハンドラー
   *
   * DataGridのソート状態が変更された際に呼び出されます。
   * ソートモデルが空の場合はIDの降順でソート、それ以外は指定されたフィールドでソートします。
   *
   * @param {GridSortModel} newSortModel - 新しいソートモデル（フィールドと昇順/降順の情報）
   * @returns {void}
   */
  const handleRowSort = useCallback(
    (newSortModel: GridSortModel) => {
      if (newSortModel.length === 0) {
        setRows([...rows].sort((a, b) => b.id - a.id));
        return;
      }

      const { field, sort } = newSortModel[0];

      setRows((prevRows) =>
        [...prevRows].sort((a, b) => {
          const aValue = a[field as keyof PropertySelectionRow];
          const bValue = b[field as keyof PropertySelectionRow];

          if ((aValue ?? 0) < (bValue ?? 0)) return sort === "asc" ? -1 : 1;
          if ((aValue ?? 0) > (bValue ?? 0)) return sort === "asc" ? 1 : -1;
          return 0;
        })
      );
    },
    [setRows, rows]
  );

  /**
   * 行削除ハンドラー
   *
   * 指定されたIDの行をグリッドから削除します。
   * 削除対象の行に対応するポップアップが開いている場合は、
   * カスタムイベントを発火してポップアップを閉じ、
   * 削除された地点のピンを再度クリックできるようにclickedLatLngをクリアします。
   *
   * @param {number[]} ids - 削除する行のID
   * @returns {void}
   */
  const handleDeleteRows = useCallback(
    (ids: number[]) => {
      const idSet = new Set(ids);
      setRows((prevRows) => {
        // pinTypeを持つ行を削除する場合は、常にポップアップを閉じる
        if (prevRows.some((row) => idSet.has(row.id) && row.pinType)) {
          // Context経由でポップアップを閉じる
          mapControl.closeActivePopup();

          // clickedLatLngをクリアして、同じピンを再度クリックできるようにする
          mapControl.clearClickedLatLng();

          // アクティブポップアップ情報をクリア
          clearActivePopup();
        }

        // 行を削除
        return prevRows.filter((row) => !idSet.has(row.id));
      });
    },
    [setRows, mapControl, clearActivePopup]
  );

  const handleDelete = useCallback(
    (id: number) => {
      handleDeleteRows([id]);
    },
    [handleDeleteRows]
  );

  // ---- チェックした物件をまとめて削除する（bulkDeletable の時だけ） ----
  const checkedRowIds = rows.filter((row) => row.selected).map((row) => row.id);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const handleConfirmBulkDelete = (): void => {
    handleDeleteRows(checkedRowIds);
    setBulkDeleteDialogOpen(false);
  };

  /**
   * タグ詳細モーダル開くハンドラー
   *
   * 選択された行のタグ詳細を表示するモーダルを開きます。
   *
   * @param {PropertySelectionRow} row - タグ詳細を表示する行のデータ
   * @returns {void}
   */
  const handleOpenTagModal = useCallback((row: PropertySelectionRow) => {
    setSelectedRowForTags(row);
    setTagModalOpen(true);
  }, []);

  /**
   * タグ詳細モーダル閉じるハンドラー
   *
   * タグ詳細モーダルを閉じ、選択された行の情報をクリアします。
   *
   * @returns {void}
   */
  const handleCloseTagModal = useCallback(() => {
    setTagModalOpen(false);
    setSelectedRowForTags(null);
  }, []);

  /**
   * 備考詳細モーダル開くハンドラー
   *
   * 選択された行の備考詳細を表示するモーダルを開きます。
   *
   * @param {PropertySelectionRow} row - 備考詳細を表示する行のデータ
   * @returns {void}
   */
  const handleOpenRemarksModal = useCallback((row: PropertySelectionRow) => {
    setSelectedRowForRemarks(row);
    setRemarksModalOpen(true);
  }, []);

  /**
   * 備考詳細モーダル閉じるハンドラー
   *
   * 備考詳細モーダルを閉じ、選択された行の情報をクリアします。
   *
   * @returns {void}
   */
  const handleCloseRemarksModal = useCallback(() => {
    setRemarksModalOpen(false);
    setSelectedRowForRemarks(null);
  }, []);

  /**
   * 追加項目詳細モーダル開くハンドラー
   */
  const handleOpenAdditionalFieldsModal = useCallback(
    (row: PropertySelectionRow) => {
      setSelectedRowForAdditionalFields(row);
      setAdditionalFieldsModalOpen(true);
    },
    []
  );

  /**
   * 追加項目詳細モーダル閉じるハンドラー
   */
  const handleCloseAdditionalFieldsModal = useCallback(() => {
    setAdditionalFieldsModalOpen(false);
    setSelectedRowForAdditionalFields(null);
  }, []);

  /**
   * タグ変更ハンドラー（表→地図ポップアップへの同期）
   *
   * 表のタグを変更したときに呼ばれる関数。
   * 表のデータを更新し、地図上のポップアップが開いていれば、そちらにも変更を通知します。
   *
   * 処理の流れ:
   * 1. 表のデータを更新
   *    - 指定されたIDの行のタグを新しいタグに置き換える
   *
   * 2. 地図のポップアップが開いているかチェック
   *    - 今変更した行のピン（赤ピンor青ピン）のポップアップが開いているか確認
   *
   * 3. 開いていれば、地図側に通知を送る
   *    - タグが変わったという通知（カスタムイベント）を送信
   *    - 赤ピンなら `dispatchOwnerPlotTagUpdate()`
   *    - 青ピンなら `dispatchMapClickTagUpdate()`
   *
   * 4. 地図側で通知を受け取る
   *    - PopupContentコンポーネントが通知を受け取り、表示を更新
   *    - これにより、表で変更したタグがポップアップにも即座に反映される
   *
   * ※カスタムイベント（通知）が必要な理由
   * 地図（Leaflet）はReactの外で動いているため、Reactの通常の方法（propsなど）では
   * 状態を共有できない。そのため、ブラウザ標準の通知機能（カスタムイベント）を使って
   * 「送信側（表）」と「受信側（地図）」で情報をやり取りしている。
   *
   * @param {number} id - 更新する行のID
   * @param {string[]} newTags - 新しいタグの配列
   * @returns {void}
   */
  // イベントハンドラーを作成
  const { handleTagUpdate, handleRemarksUpdate, handleAdditionalFieldsUpdate } =
    createPropertySelectionEventHandlers(
      findRealEstateSearchIndex,
      findReceptionBookIndex
    );

  const handleTagsChange = useCallback(
    (id: number, newTags: string[]) => {
      setRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          row.id === id ? { ...row, tags: newTags } : row
        );

        const updatedRow = updatedRows.find((row) => row.id === id);
        if (updatedRow) {
          handleTagUpdate(updatedRow, newTags);
        }

        return updatedRows;
      });
    },
    [setRows, handleTagUpdate]
  );

  /**
   * 備考変更ハンドラー（表→地図ポップアップへの同期）
   *
   * 表の備考を変更したときに呼ばれる関数。
   * タグ変更ハンドラーと同様の仕組みで動作します。
   *
   * 処理の流れ:
   * 1. 表のデータを更新
   *    - 指定されたIDの行の備考を新しい備考に置き換える
   *
   * 2. 地図のポップアップが開いているかチェック
   *    - 今変更した行のピンのポップアップが開いているか確認
   *
   * 3. 開いていれば、地図側に備考変更を通知
   *    - カスタムイベントを使って備考が変わったことを送信
   *    - ポップアップ側がイベントを受け取り、表示を更新
   *
   * @param {number} id - 更新する行のID
   * @param {string} newRemarks - 新しい備考
   * @returns {void}
   */
  const handleRemarksChange = useCallback(
    (id: number, newRemarks: string) => {
      setRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          row.id === id ? { ...row, remarks: newRemarks } : row
        );

        const updatedRow = updatedRows.find((row) => row.id === id);
        if (updatedRow) {
          handleRemarksUpdate(updatedRow, newRemarks);
        }

        return updatedRows;
      });
    },
    [setRows, handleRemarksUpdate]
  );

  /**
   * 追加項目変更ハンドラー（表→地図ポップアップへの同期）
   *
   * 表の追加項目を変更したときに呼ばれる関数。
   * タグ・備考変更ハンドラーと同様の仕組みで動作します。
   *
   * @param {number} id - 更新する行のID
   * @param {string} newAdditionalFields - 新しい追加項目（JSON文字列）
   * @returns {void}
   */
  const handleAdditionalFieldsChange = useCallback(
    (id: number, newAdditionalFields: string) => {
      setRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          row.id === id
            ? { ...row, additionalFields: newAdditionalFields }
            : row
        );

        const updatedRow = updatedRows.find((row) => row.id === id);
        if (updatedRow) {
          handleAdditionalFieldsUpdate(updatedRow, newAdditionalFields);
        }

        return updatedRows;
      });
    },
    [setRows, handleAdditionalFieldsUpdate]
  );

  /**
   * カスタムフッターコンポーネント
   *
   * グリッドのフッターに表示されるコンポーネントです。
   */
  const Footer: React.FC = () => {
    return <CustomFooter rows={rows} />;
  };

  // カラム設定
  const columns: GridColDef[] = [
    {
      field: "id",
      width: 30,
      align: "center",
    },
    {
      field: "bookType",
      headerName: "不動産種別",
      width: 100,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return convertTypeEnglishToJapanese(String(value));
      },
    },
    {
      field: "prefName",
      headerName: "都道府県",
      width: 80,
      sortable: true,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "location",
      headerName: "所在",
      width: 240,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        // 長い文字列の場合は折り返す
        return <Box sx={{ whiteSpace: "normal" }}>{value}</Box>;
      },
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 100,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return convertFullWidthDigitsAndHyphenToHalfWidth(value as string);
      },
      renderHeader: (_params: GridColumnHeaderParams) => (
        <strong>
          地番または
          <br />
          家屋番号
        </strong>
      ),
    },
    {
      field: "area",
      headerName: "推計地積(㎡)",
      width: 100,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return formatArea(value as number);
      },
      renderHeader: () => (
        <strong>
          推計地積
          <br />
          (㎡)
        </strong>
      ),
    },
    {
      field: "tags",
      headerName: "タグ",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const tagCount = (params.value as string[])?.length || 0;
        const row = rows.find((r) => r.id === params.id);
        return (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              if (row) {
                handleOpenTagModal(row);
              }
            }}
            sx={{ cursor: "pointer" }}
          >
            詳細{tagCount > 0 && ` (${tagCount}個)`}
          </Link>
        );
      },
      renderHeader: () => <strong>タグ</strong>,
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const row = rows.find((r) => r.id === params.id);
        return (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              if (row) {
                handleOpenRemarksModal(row);
              }
            }}
            sx={{ cursor: "pointer" }}
          >
            編集
          </Link>
        );
      },
      renderHeader: () => <strong>備考</strong>,
    },
    ...(hasAvailableFields
      ? [
          {
            field: "additionalFields",
            headerName: "カスタム項目",
            width: 120,
            sortable: false,
            align: "center" as const,
            headerAlign: "center" as const,
            renderCell: (params: GridRenderCellParams) => {
              const row = rows.find((r) => r.id === params.id);
              return (
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row) {
                      handleOpenAdditionalFieldsModal(row);
                    }
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  編集
                </Link>
              );
            },
            renderHeader: () => <strong>カスタム項目</strong>,
          },
        ]
      : []),
    {
      field: "action",
      headerName: "Action",
      minWidth: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            <MuiButton
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
              color={ButtonColorOption.Error}
              onClick={() => {
                handleDelete(params.id as number);
              }}
            >
              削除する
            </MuiButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          height,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {bulkDeletable && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MuiButton
              variant="outlined"
              color="error"
              disabled={checkedRowIds.length === 0}
              onClick={() => {
                setBulkDeleteDialogOpen(true);
              }}
              sx={{
                ...MAP_CONTROL_TEXT_SX,
                height: MAP_BUTTON_HEIGHT,
                whiteSpace: "nowrap",
              }}
            >
              一括削除する（{checkedRowIds.length}件）
            </MuiButton>
          </Box>
        )}
        <StyledDataGrid
          disableColumnMenu
          disableSelectionOnClick={true}
          rows={rows}
          columns={columns}
          checkboxSelection
          selectionModel={selectedRowIds}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "desc" }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                tags: crm,
                remarks: crm,
              },
            },
          }}
          // 地図画面の左の列に合わせて、行・見出し・文字を小さくする
          headerHeight={MAP_GRID_ROW_HEIGHT_PX}
          rowHeight={MAP_GRID_ROW_HEIGHT_PX}
          sx={{
            // 上の削除ボタンの分を除いた高さいっぱいに広げる
            flex: 1,
            minHeight: 0,
            fontSize: MAP_CONTROL_TEXT_SX.fontSize,
            // セルのリンク（タグ・備考の「詳細」）とフッターの合計も表の文字に合わせる
            "& .MuiTypography-root": { fontSize: "inherit" },
            "& .MuiCheckbox-root .MuiSvgIcon-root": { fontSize: 18 },
            // チェックした行には色を付けない（チェックボックスで分かる）。色は地図のピンと対応させた行だけに付ける
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent",
            },
            "& .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "action.hover",
            },
            [`& .MuiDataGrid-row.${FOCUSED_ROW_CLASS}, & .MuiDataGrid-row.${FOCUSED_ROW_CLASS}:hover`]:
              {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.14),
              },
            ...(onRowFocus && { "& .MuiDataGrid-row": { cursor: "pointer" } }),
          }}
          apiRef={apiRef}
          getRowClassName={(params) =>
            params.id === focusedRowId ? FOCUSED_ROW_CLASS : ""
          }
          onCellClick={handleCellClick}
          onSortModelChange={handleRowSort}
          onSelectionModelChange={handleRowSelectionChange}
          components={{
            Footer,
          }}
        />
      </Box>
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={(_event, reason) => {
          // 誤タップで閉じない。閉じるのはキャンセル・削除するのボタンだけ
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setBulkDeleteDialogOpen(false);
        }}
        fullScreen={isMobile}
      >
        <DialogTitle>チェックした物件を削除する</DialogTitle>
        <DialogContent>
          <DialogContentText>
            チェックした{checkedRowIds.length}
            件の物件を物件リストから削除します。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setBulkDeleteDialogOpen(false);
            }}
          >
            キャンセル
          </MuiButton>
          <MuiButton
            variant="contained"
            color="error"
            onClick={handleConfirmBulkDelete}
          >
            削除する
          </MuiButton>
        </DialogActions>
      </Dialog>
      <TagDetailModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        rowData={selectedRowForTags}
        onSave={handleTagsChange}
      />
      <RemarksDetailModal
        open={remarksModalOpen}
        onClose={handleCloseRemarksModal}
        rowData={selectedRowForRemarks}
        onSave={handleRemarksChange}
      />
      <AdditionalFieldsModal
        open={additionalFieldsModalOpen}
        onClose={handleCloseAdditionalFieldsModal}
        availableFields={availableFields}
        initialValues={(() => {
          const row = rows.find(
            (r) => r.id === selectedRowForAdditionalFields?.id
          );
          return convertStringToAdditionalFields(
            row?.additionalFields,
            availableFields
          );
        })()}
        onSave={(data) => {
          if (selectedRowForAdditionalFields && availableFields) {
            const convertedData = convertFormDataToAdditionalFields(
              data,
              availableFields
            );
            const stringified = JSON.stringify(convertedData);
            handleAdditionalFieldsChange(
              selectedRowForAdditionalFields.id,
              stringified
            );
            handleCloseAdditionalFieldsModal();
          }
        }}
      />
    </>
  );
};
